import React, { Children, PureComponent, ReactElement } from 'react';

import { ColumnType, DataTableColumnAPI, SortOrder } from '@philips-emr/tws-components-api';
import { TextAlignment } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { arrayOf, bool, color, element, eventListener, expression, formatters, number, oneOf, oneOfType, record, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { ClickEvent, SortEvent } from '@philips-emr/tws-core/lib/component/types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, enumValues, isEnumValid, isFunction } from '@philips-emr/tws-utils';

import { DataTableCell } from '.';

export interface Formatters {
  value?: { name: string; options?: Record<string, any> };
}

export interface Superscription {
  color: string;
  label: string;
  value: string;
}

export interface DataTableColumnProps {
  alignment?: TextAlignment;
  children?: ReactElement | ReactElement[] | string;
  className?: string;
  entry?: string;
  focused?: boolean;
  formatters?: Formatters;
  headerAlignment?: TextAlignment;
  key?: string;
  onClick?: (event: ClickEvent) => void;
  onContextMenuClick?: (event: ClickEvent) => void;
  onDoubleClick?: (event: ClickEvent) => void;
  onSort?: (event: SortEvent) => void;
  showSuperscriptionLabel?: boolean;
  sortDirection?: SortOrder;
  sortable?: boolean;
  superscription?: Superscription[];
  type: ColumnType;
  value: string;
  visible?: boolean;
  width: number;
}

@AwesomeComponent({ type: ComponentType.LIST, api: DataTableColumnAPI })
class DataTableColumn extends PureComponent<DataTableColumnProps> {
  static propTypes = {
    alignment: oneOf(enumValues(TextAlignment)),
    children: oneOfType([element, arrayOf(element)]),
    className: string,
    entry: string,
    focused: bool,
    formatters: formatters,
    headerAlignment: oneOf(enumValues(TextAlignment)),
    onClick: eventListener,
    onContextMenuClick: eventListener,
    onDoubleClick: eventListener,
    onSort: eventListener,
    showSuperscriptionLabel: bool,
    sortDirection: oneOf(enumValues(SortOrder)),
    sortable: bool,
    superscription: record({ color: color, value: string, label: string }),
    type: oneOf(enumValues(ColumnType)),
    value: expression,
    visible: bool,
    width: number,
  };

  static defaultProps = {
    alignment: TextAlignment.CENTER,
    focused: false,
    headerAlignment: TextAlignment.LEFT,
    sortable: true,
    type: ColumnType.DEFAULT,
    value: 'Column',
    visible: true,
  };

  onColumnClick = (): void => {
    const { entry = '', onClick, onSort, sortDirection = '', sortable } = this.props;

    if (isFunction(onClick)) {
      onClick(new ClickEvent({ value: entry }));
    }

    if (sortable && isFunction(onSort)) {
      onSort(new SortEvent({ name: entry, direction: sortDirection }));
    }
  };

  getValue(): ReactElement | ReactElement[] | null | string {
    const { children, type, value } = this.props;

    if (type === ColumnType.CHEVRON) {
      return null;
    }

    if (children && Children.count(children) > 0) {
      return children;
    }

    return msg(value);
  }

  getWidth(): number {
    const { type, width } = this.props;

    if (type === ColumnType.CHEVRON) {
      return 72;
    }

    return width;
  }

  getClassName(): string {
    const { className = '', focused = false, sortDirection } = this.props;

    return classNames({
      'c-data-table__column': true,
      'is-focused': focused,
      [`sort-${sortDirection}`]: isEnumValid(SortOrder, sortDirection),
      [className]: Boolean(className),
    });
  }

  render(): ReactElement {
    const { headerAlignment, type } = this.props;

    return (
      <DataTableCell
        alignment={headerAlignment}
        className={this.getClassName()}
        onClick={this.onColumnClick}
        type={type}
        width={this.getWidth()}
      >
        {this.getValue()}
      </DataTableCell>
    );
  }
}

export default DataTableColumn;
